import { MIN_PAID_COMPETITION_PRICE_CENTS } from '@shared/constants';
import {
  CompetitionLevel,
  CompetitionType,
  FederalAgeCategory,
  FederalClassification,
  SportType,
} from '@shared/enums/domain.enum';
import { z } from 'zod';

// ─── Form state (partagé Create + Edit) ───────────────────────────────────────
export interface CompetitionFormState {
  name: string;
  alias: string;
  type: CompetitionType | '';
  level: CompetitionLevel | '';
  sportType: SportType;
  date: string;
  registrationEnd: string;
  registrationOpensAt: string;
  maxTeams: string;
  minTeams: string;
  playersPerTeam: number;
  price: string;
  venue: string;
  description: string;
  restrictedToMembers: boolean;
  restrictedAffiliates: boolean;
  requireTeamName: boolean;
  addressLabel: string;
  city: string;
  department: string;
  postcode: string;
  citycode: string;
  latitude: number | null;
  longitude: number | null;
  allowedAgeCategories: FederalAgeCategory[];
  allowedClassifications: FederalClassification[];
}

// ─── UI config (options de sélection + classe CSS base) ───────────────────────
export const PLAYERS_PER_TYPE: Record<CompetitionType, number> = {
  [CompetitionType.SINGLE]: 1,
  [CompetitionType.DOUBLE]: 2,
  [CompetitionType.TRIPLE]: 3,
};

export const TYPE_OPTIONS: { value: CompetitionType; label: string }[] = [
  { value: CompetitionType.SINGLE, label: 'Tête-à-tête (1 joueur)' },
  { value: CompetitionType.DOUBLE, label: 'Doublette (2 joueurs)' },
  { value: CompetitionType.TRIPLE, label: 'Triplette (3 joueurs)' },
];

export const LEVEL_OPTIONS: { value: CompetitionLevel; label: string }[] = [
  { value: CompetitionLevel.LOCAL, label: 'Local' },
  { value: CompetitionLevel.DEPARTMENTAL, label: 'Départemental' },
  { value: CompetitionLevel.REGIONAL, label: 'Régional' },
  { value: CompetitionLevel.NATIONAL, label: 'National' },
  { value: CompetitionLevel.CHAMPIONSHIP, label: 'Championnat' },
  { value: CompetitionLevel.PROMOTION, label: 'Promotion' },
  { value: CompetitionLevel.MASTER, label: 'Master' },
  { value: CompetitionLevel.PRESTIGE, label: 'Prestige' },
];

export const SPORT_OPTIONS: { value: SportType; label: string }[] = [
  { value: SportType.PETANQUE, label: 'Pétanque' },
  { value: SportType.LYONNAISE, label: 'Boule lyonnaise' },
  { value: SportType.PROVENCAL, label: 'Jeu provençal' },
  { value: SportType.TOUS, label: 'Tous les sports de boules' },
];

export const BASE_INPUT_CLASS = 'w-full rounded-lg border px-4 py-3 focus:border-blue-500 focus:outline-none';

// ─── Constantes de tarification (aperçu frais dans le formulaire) ─────────────
// Commission BoulesConnect : voir `computeCommissionCents` (@shared/utils) —
// formule plancher/fixe, NE PAS dupliquer le taux ici.
export const STRIPE_RATE_BPS = 150; // Frais Stripe : 1,5 %
export const STRIPE_FIXED_CENTS = 25; // Frais Stripe fixes : 0,25 €
// Prix minimum d'un concours payant, en euros (dérivé de la constante shared).
const MIN_PAID_PRICE_EUROS = MIN_PAID_COMPETITION_PRICE_CENTS / 100;

// ─── Base schema (shape accessible pour validation champ par champ) ────────────
const baseCompetitionFormSchema = z.object({
  name: z.string().min(3, 'Minimum 3 caractères'),
  alias: z
    .string()
    .min(3, 'Minimum 3 caractères')
    .regex(/^[a-z0-9-]+$/, 'Lettres minuscules, chiffres et tirets uniquement'),
  type: z.nativeEnum(CompetitionType, {
    error: () => ({ message: 'Sélectionner un type de concours' }),
  }),
  level: z.nativeEnum(CompetitionLevel, {
    error: () => ({ message: 'Sélectionner un niveau' }),
  }),
  sportType: z.nativeEnum(SportType),
  date: z.string().min(1, 'Date du concours obligatoire'),
  registrationEnd: z.string().min(1, 'Date de clôture obligatoire'),
  // Feature 7 (2026-08) — date d'ouverture des inscriptions (optionnelle).
  // Vide = inscriptions ouvertes dès la publication.
  registrationOpensAt: z.string().optional(),
  maxTeams: z
    .string()
    .min(1, "Nombre maximum d'équipes obligatoire")
    .refine(v => !isNaN(Number(v)) && Number(v) >= 2, 'Minimum 2 équipes'),
  minTeams: z
    .string()
    .refine(v => v === '' || (!isNaN(Number(v)) && Number(v) >= 2), 'Minimum 2 équipes')
    .optional(),
  playersPerTeam: z.number(),
  price: z
    .string()
    .refine(
      v => v === '' || Number(v) === 0 || (!isNaN(Number(v)) && Number(v) >= MIN_PAID_PRICE_EUROS),
      `Prix invalide : gratuit (vide ou 0) ou minimum ${MIN_PAID_PRICE_EUROS} €`,
    ),
  venue: z.string().min(2, 'Nom du lieu obligatoire (min. 2 caractères)'),
  description: z.string().optional(),
  restrictedToMembers: z.boolean(),
  restrictedAffiliates: z.boolean(),
  requireTeamName: z.boolean(),
  addressLabel: z.string().optional(),
  city: z.string().optional(),
  department: z.string().optional(),
  postcode: z.string().optional(),
  citycode: z.string().optional(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  allowedAgeCategories: z.array(z.nativeEnum(FederalAgeCategory)),
  allowedClassifications: z.array(z.nativeEnum(FederalClassification)),
});

// ─── Schema complet avec règle inter-champs ────────────────────────────────────
export const CompetitionFormSchema = baseCompetitionFormSchema.superRefine((data, ctx) => {
  if (data.registrationEnd && data.date) {
    // Comparaison à l'instant (date + heure) : registrationEnd et date sont des
    // datetime-local. La clôture ne peut pas tomber après le début du concours
    // (sinon les inscriptions resteraient ouvertes après le coup d'envoi, et le
    // workflow Temporal fermerait après-coup). Backstop au submit — le cap onBlur
    // évite normalement de l'atteindre.
    const regEnd = new Date(data.registrationEnd).getTime();
    const comp = new Date(data.date).getTime();
    if (!Number.isNaN(regEnd) && !Number.isNaN(comp) && regEnd > comp) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La clôture des inscriptions ne peut pas être après le début du concours',
        path: ['registrationEnd'],
      });
    }
  }
  // Feature 7 (2026-08) — l'ouverture des inscriptions doit précéder leur
  // clôture (sinon la fenêtre d'inscription serait vide/incohérente).
  if (data.registrationOpensAt && data.registrationEnd) {
    const opens = new Date(data.registrationOpensAt).getTime();
    const closes = new Date(data.registrationEnd).getTime();
    if (!Number.isNaN(opens) && !Number.isNaN(closes) && opens > closes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "L'ouverture des inscriptions doit précéder leur clôture",
        path: ['registrationOpensAt'],
      });
    }
  }
  if (data.minTeams && data.maxTeams) {
    const min = Number(data.minTeams);
    const max = Number(data.maxTeams);
    if (!isNaN(min) && !isNaN(max) && min > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Le minimum doit être ≤ au maximum',
        path: ['minTeams'],
      });
    }
  }
});

export type CompetitionFormData = z.infer<typeof CompetitionFormSchema>;
export type CompetitionFormErrors = Partial<Record<keyof CompetitionFormData, string>>;

// ─── Validation champ par champ (onBlur) ──────────────────────────────────────
export function validateCompetitionField(key: keyof CompetitionFormData, value: unknown): string | undefined {
  const fieldSchema = baseCompetitionFormSchema.shape[key as keyof typeof baseCompetitionFormSchema.shape];
  if (!fieldSchema) return undefined;
  const result = (fieldSchema as z.ZodTypeAny).safeParse(value);
  if (!result.success) {
    return result.error.issues[0]?.message;
  }
  return undefined;
}

// ─── Validation minimale pour brouillon ──────────────────────────
// Pour créer un brouillon, seul le nom est requis (min 3 caractères).
// L'alias est auto-généré côté form (slug du name) ou côté server si absent.
// Tous les autres champs peuvent rester vides et seront validés plus tard
// lors de la publication (POST /competitions/:id/publish).
export function validateCompetitionDraftMinimal(data: Pick<CompetitionFormState, 'name'>): CompetitionFormErrors {
  const result = baseCompetitionFormSchema.shape.name.safeParse(data.name);
  if (result.success) return {};
  return { name: result.error.issues[0]?.message };
}
